import {
  computed,
  observable,
  extendObservable,
  intercept,
  observe,
  isObservable,
  toJS
} from 'mobx'
import Validation, { ValidationInvalidError } from '../../utils/validation'

/* eslint-disable no-underscore-dangle */
// import _debug from 'debug'
// const debug = _debug('app:validation')

const resolveResult = (valid, validator, value, model) => {
  let errors = null

  if (!valid) {

    errors = Validation.validationErrors(
      validator,
      value,
      model
    )

    if (typeof validator !== 'string') {
      errors.valid = valid
      return errors
    }

  }

  return {
    valid,
    errors
  }
}

const performValidation = (model, value, validator) => {

  const valid = Validation.isValid(
    validator,
    value,
    model
  )

  // in case the result is a promise (e.g. async validation)
  if (valid.then) {
    return {
      valid: false,
      promise: valid.then((result) => {
        return resolveResult(result, validator, value, model)
      })
    }
  }

  return resolveResult(valid, validator, value, model)
}

const ensureKeyForObservable = (obj, origKey = '') => {

  let key = origKey

  const parts = key.split('.')

  if (parts.length > 1) {
    key = parts.pop()
    obj = parts.reduce((memo, part) => memo[part], obj)
  }

  if (!obj) {
    console.warn(`Observable for \`${origKey}\` not found!`)
  }

  // check if the value with given key (or _key) is observable at all
  let isObservableValue = isObservable(obj, key)
  if (!isObservableValue) {
    key = `_${key}`
    isObservableValue = isObservable(obj, key)
    if (!isObservableValue) {
      // eslint-disable-next-line max-len
      console.warn(`Validator Warning: There is no observable \`${key}\` or \`_${key}\` in \`${obj.constructor.name}\`! Not registering for \`${key}\`.`)
    }
  }

  return [key, isObservableValue, obj]

}

const createEnforcer = (obj, key, validator) => {
  intercept(obj, key, (change) => {
    try {
      change.newValue = Validation.ensure(validator, change.newValue)
    }
    catch (ex) {
      if (ex instanceof ValidationInvalidError) {
        // ignore the new value
        return null
      }
      console.error('Unknown Validation Error:', ex)
    }

    return change
  })
}

export default function validate(
  validators,
  customValidationMethods = {},
  customValidationTexts = {}
) {

  Validation.add(customValidationMethods)
  Validation.addTexts(customValidationTexts)

  return (target) => {

    const provider = target.method
      ? target
      : target.prototype

    // eslint-disable-next-line no-shadow
    provider.validate = function validate() {
      Object.keys(validators).forEach((valKey) => {
        const validator = validators[valKey]
        const [key, isObservableValue, obj] = ensureKeyForObservable(this, valKey)
        if (isObservableValue) {
          this.validFields[valKey] = performValidation(this, obj[key], validator)

          if (this.validFields[valKey].promise) {
            this.validFields[valKey].promise.then((valid) => {
              this.validFields[valKey] = valid
            })
          }
        }
      })
    }

    // eslint-disable-next-line complexity
    provider.setValid = function setValid(name, { field, state }) {

      if (this.validFields) {

        const fieldKeys = field.split('.')
        const lastKey = fieldKeys.pop()
        let key
        let currentField = this.validFields

        key = fieldKeys.shift()
        while (key) {
          currentField = currentField[key] || {}
          key = fieldKeys.shift()
        }

        currentField[lastKey] = currentField[lastKey] || {}

        // const setValidErrors = (currentField[lastKey].errors || []).filter(error => error._name === name)
        if (
          // make sure we do not validate invalid state,
          // unless the invalid state was caused by this
          // validation
          currentField[lastKey].valid /* || // TODO: figure out what that comparison is for
          setValidErrors.length === setValidErrors.length */
        ) {
          currentField[lastKey].valid = state.valid
        }

        const { errors } = currentField[lastKey]
        let newErrors = [...(toJS(errors) || [])]

        let match = newErrors.find((error) => error._name === name)
        while (match) {
          const index = newErrors.indexOf(match)
          newErrors = [
            ...newErrors.slice(0, index),
            ...newErrors.slice(index + 1)
          ]
          match = newErrors.find((error) => error._name === name)
        }

        if (state.errors) {
          newErrors = [
            ...(newErrors || []),
            ...state.errors.map((error) => ({ ...error, _name: name }))
          ]
        }

        currentField[lastKey].errors = newErrors.length === 0
          ? null
          : newErrors

      }

    }

    provider.initValidate = function initValidate() {

      const keys = Object.keys(validators)

      this.validFields = observable(keys.reduce((memo, key) => {
        memo[key] = true
        return memo
      }, {}))

      // this is the same as @computed get isValid() {...}
      // @see http://mobxjs.github.io/mobx/refguide/computed-decorator.html
      extendObservable(this, {
        isValid: computed(() => {
          return Object.keys(this.validFields)
            .every((field) => this.validFields[field].valid)
        })
      })

      // const observeObj = (baseObj, keys, baseKey = '') => {
      //   keys
      //   .map(key => ({
      //     parts: key.split('.'),
      //     complete: key
      //   }))
      //   .map(({ parts: [valKey, rest], complete }) => {
      //     if (!rest || !rest.length) {
      //
      //       const validator = validators[valKey]
      //       const [key, isObservableValue, obj] = ensureKeyForObservable(
      //         baseObj,
      //         valKey
      //       )
      //
      //       // register enforcers. Enforcers act like validators, but if a value is
      //       // inorrect, they modify it to a correct one
      //       if (isObservableValue) {
      //
      //         // if the validator contains a spec for an enforcer,
      //         // create the enforcer
      //         if (Validation.isEnforce(validator)) {
      //           createEnforcer(obj, key, validator)
      //         }
      //
      //         // start observing for changes to apply validation
      //         observe(obj, key, (newValue) => {
      //           this.validFields[complete] = performValidation(obj, newValue, validator)
      //         })
      //       }
      //
      //     }
      //     else {
      //
      //       observe(baseObj, valKey, (newValue) => {
      //         observeObj(newValue, baseKey)
      //       })
      //       return [baseObj[valKey], [baseKey, valKey].filter(k => !!k).join('.')]
      //     }
      //   })
      //   .filter((rest) => !!rest)
      //   .map(([obj, baseKey]) => observeObj(obj, baseKey))
      // }
      //
      // observeObj(this, keys, '')

      keys.forEach((valKey) => {

        const validator = validators[valKey]
        const [key, isObservableValue, obj] = ensureKeyForObservable(
          this,
          valKey
        )

        // register enforcers. Enforcers act like validators, but if a value is
        // inorrect, they modify it to a correct one
        if (isObservableValue) {

          // if the validator contains a spec for an enforcer,
          // create the enforcer
          if (
            typeof validator === 'string'
            && Validation.isEnforce(validator)
          ) {
            createEnforcer(obj, key, validator)
          }

          // start observing for changes to apply validation
          observe(obj, key, (newValue) => {
            const result = performValidation(this, newValue, validator)
            Object.keys(validator).forEach((vali) => {
              if (vali in result && result[vali].then) {
                // noop
              }
              this.validFields[key] = {
                ...result
              }
            })
            this.validFields[key] = result
          })
        }

      })

      this.validate()

    }

  }

}

/* eslint-enable no-underscore-dangle */



// WEBPACK FOOTER //
// ./src/shared/decorators/model/validate.js